import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Card, CircularProgress, Stack, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { FormBlock } from '../../../../common/FormGenerator'
import { getFormConfig } from './settingsConfig'
import {
  growthToolFormSchema,
  resolveGrowthToolContentTypes,
  resolveGrowthToolSourceTypes,
} from '../../CreateGrowthTool/ZodValidationSchema'
import type { GrowthToolFormValues } from '../../CreateGrowthTool/ZodValidationSchema'
import { CardStyle, TypStyle } from './style'
import { useGetGrowthToolQuery, useUpdateGrowthToolMutation } from '../../../../api/growthTools'
import { extractApiErrorMessage } from '../../../../utils/api'
import { isGrowthToolFormUnchanged } from '../../../../utils/growthTool/areGrowthToolFormOutputsEqual'

const editDescriptionSchema = growthToolFormSchema.omit({ image: true })
type EditDescriptionFormValues = Omit<GrowthToolFormValues, 'image'>

export const EditGrowthToolForm = () => {
  const { id } = useParams<{ id: string }>()
  const toolId = id ? parseInt(id, 10) : NaN
  const { data: initialData } = useGetGrowthToolQuery({ id: toolId }, { skip: Number.isNaN(toolId) })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [updateGrowthTool] = useUpdateGrowthToolMutation()
  const baselineRef = useRef<EditDescriptionFormValues | null>(null)

  const methods = useForm<EditDescriptionFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(editDescriptionSchema),
    defaultValues: {
      title: '',
      description: '',
      source_type: [],
      content_type: [],
    },
  })

  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (!initialData) return
    const values: EditDescriptionFormValues = {
      title: initialData.title,
      description: initialData.description,
      source_type: resolveGrowthToolSourceTypes(initialData.source_type),
      content_type: resolveGrowthToolContentTypes(initialData.content_type),
    }
    reset(values)
    baselineRef.current = values
  }, [initialData, reset])

  const onSubmit = async (data: EditDescriptionFormValues) => {
    if (Number.isNaN(toolId)) return

    if (baselineRef.current != null && isGrowthToolFormUnchanged(data, baselineRef.current)) {
      toast.info('Нет изменений')
      return
    }

    setIsSubmitting(true)
    try {
      await updateGrowthTool({
        id: toolId,
        payload: {
          title: data.title,
          description: data.description,
          source_type: data.source_type,
          content_type: data.content_type,
          status: 'published',
        },
      }).unwrap()
      baselineRef.current = data
      toast.success('Инструмент личностного роста сохранён')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить изменения')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!initialData) {
    return null
  }

  return (
    <FormProvider {...methods}>
      <Stack gap={2} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Название
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('title')} />
        </Card>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Описание
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('description')} />
        </Card>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Происхождение материала
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('source_type')} />
        </Card>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Формат материала
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('content_type')} />
        </Card>

        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="follow" disabled={isSubmitting}>
            {isSubmitting ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Сохранение...</span>
              </Stack>
            ) : (
              'Сохранить'
            )}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  )
}
